import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FeedbackContext } from './shared/contexts/FeedbackContext';
import { FullPageLoadingSpinner, LoadingSpinner } from './shared/components/LoadingSpinner';
import { put } from './util/axios';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { getPasswordError, getPasswordRules } from './util/commonUtilities';
import PasswordChecklist from './shared/components/PasswordChecklist';


const InvitedUserRegisterForm = () => {
  const { showErrorToast } = useContext(FeedbackContext);
  const { email_signup_token } = useParams();

  const [loading, setLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(true);
  const [onSuccess, setOnSuccess] = useState(false)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordSpecialChars, setPasswordSpecialChars] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const passwordRules = getPasswordRules(password, passwordSpecialChars);
  const allPasswordRulesMet = passwordRules.every(rule => rule.met);

  const handleSetPassword = e => {
    setPasswordErrorMessage('');
    setPassword(e.target.value);
  }

  useEffect(() => {
    axios.get(`/api/invite-user-link/${email_signup_token}`)
      .then(({ data }) => {
        setLoading(false);
        setIsExpired(false);
        setEmail(data.email);
        if (data.firstName) setFirstName(data.firstName);
        if (data.lastName) setLastName(data.lastName);
        setPasswordSpecialChars(data.passwordSpecialChars || '');
      })
      .catch((error) => {
        const { response } = error;
        setLoading(false);
        if (response) {
          if (response.status === 401) return setIsExpired(true);
          if (response.status === 422) return showErrorToast(response.data);
          return showErrorToast(`error code ${response.status} received`);
        } else {
          showErrorToast('Could not successfully complete request');
        }
      });
  }, []);

  const invitedUserSetPassword = (data) => {
    const onSuccess = res => {
      if (res?.status === 200)
        showSuccessToast("User Added  Sucessfully")
      setLoading(false);
      setOnSuccess(true)
    };
    const onError = err => {
      setLoading(false)
      showErrorToast(err);
    }
    put(`/api/invite-user/update`, data, onSuccess, onError);
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const passwordError = getPasswordError(password, passwordSpecialChars);
    if (passwordError) {
      setPasswordErrorMessage(passwordError);
      return;
    }
    setPasswordErrorMessage('');
    setLoading(true);
    const emailSignupToken = email_signup_token;
    invitedUserSetPassword({ emailSignupToken, password })
  }


  if (loading && isExpired) return <FullPageLoadingSpinner />;

  if (isExpired) return (
    <main className="p-10 text-lg bg-white">
      <p>This link has expired</p>
    </main>
  );


  if (onSuccess) return (
    <main className="p-10 text-lg bg-white">
      <p>Registration Successful</p>
    </main>
  );

  return (
    <main className="relative p-10 text-center bg-white h-full">
      <div className="w-full px-4 py-5 m-auto text-gray-500 border border-gray-300 rounded shadow-sm max-w-400px">
        <form className="flex flex-col items-center justify-center w-full">
          <div className="flex flex-col w-full mt-4">
            <label htmlFor="email" className="mb-1 font-bold text-center text-black">Email</label>
            <input
              className="text-black disabled:cursor-not-allowed"
              id="email"
              type="text"
              placeholder="Email"
              defaultValue={email}
              disabled
            />
          </div>
          <div className="flex flex-col w-full mt-4">
            <label htmlFor="firstName" className="mb-1 font-bold text-center text-black">First Name</label>
            <input
              className="text-black"
              id="firstName"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              disabled
            />
          </div>
          <div className="flex flex-col w-full mt-4">
            <label htmlFor="lastName" className="mb-1 font-bold text-center text-black">Last Name</label>
            <input
              className="text-black"
              type="text"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              disabled
            />
          </div>
          <div className="flex flex-col w-full mt-4">
            <label htmlFor="password" className="mb-1 font-bold text-center text-black">Password</label>
            <div className="relative w-full">
              <input
                className="w-full pr-10 text-black"
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handleSetPassword}
                required
              />
              <button
                type="button"
                className="absolute top-1/2 right-3 transform -translate-y-1/2"
                onClick={() => setShowPassword(prev => !prev)}
              >
                {showPassword ? <EyeOffIcon className="w-6 h-6" /> : <EyeIcon className="w-6 h-6" />}
              </button>
            </div>
            <PasswordChecklist rules={passwordRules} />
            {!!passwordErrorMessage && (
              <span className="mt-1 text-sm text-left text-red-500">{passwordErrorMessage}</span>
            )}
          </div>
          <input
            type="submit"
            className="w-full p-2 mt-4 text-lg font-bold text-white rounded cursor-pointer bg-teal hover:bg-tealDark disabled:hover:bg-teal disabled:cursor-not-allowed disabled:opacity-50"
            onClick={(e) => handleSubmit(e)}
            disabled={!email || !allPasswordRulesMet || loading}
            value="Sign Up"
          />
        </form>
      </div>
      {loading && <LoadingSpinner />}
    </main>
  );
}

export default InvitedUserRegisterForm;
